import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useGetVouchersQuery } from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const VOUCHER_TYPE_LABELS = {
  payment_voucher: 'Payment Voucher',
  receipt_voucher: 'Receipt Voucher',
  journal_voucher: 'Journal Voucher',
  voucher: 'Journal Voucher',
};

function VouchersList() {
  const navigate = useNavigate();
  const toApiDate = (d) => {
    if (!d) return undefined;
    const date = new Date(d);
    if (Number.isNaN(date.getTime())) return undefined;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [voucherType, setVoucherType] = useState('');
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const { data, isLoading } = useGetVouchersQuery({
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
    voucherType: voucherType || undefined,
    limit: 100,
  });
  const vouchers = data?.vouchers || [];

  const formatAmount = (v) => {
    const n = Number(v || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '–');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Vouchers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/finance/vouchers/new')}>
            New Voucher
          </Button>
          <Button variant="outlined" onClick={() => navigate('/finance/vouchers/new?type=payment')}>
            Payment Voucher
          </Button>
          <Button variant="outlined" onClick={() => navigate('/finance/vouchers/new?type=receipt')}>
            Receipt Voucher
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={(newValue) => setFromDate(newValue)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={(newValue) => setToDate(newValue)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </LocalizationProvider>
        <TextField
          select
          label="Type"
          value={voucherType}
          onChange={(e) => setVoucherType(e.target.value)}
          size="small"
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="payment">Payment Voucher</MenuItem>
          <MenuItem value="receipt">Receipt Voucher</MenuItem>
          <MenuItem value="journal">Journal Voucher</MenuItem>
        </TextField>
      </Box>

      <Card>
        <CardContent>
          {isLoading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : vouchers.length === 0 ? (
            <Typography color="text.secondary">No vouchers in this period.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payee</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Note</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vouchers.map((v) => {
                    const totalDebit = (v.lines || []).reduce((s, l) => s + Number(l.debit || 0), 0);
                    return (
                      <TableRow key={v.id} hover>
                        <TableCell>{formatDate(v.entryDate)}</TableCell>
                        <TableCell>{VOUCHER_TYPE_LABELS[v.referenceType] || v.referenceType || '–'}</TableCell>
                        <TableCell>{v.payee || '–'}</TableCell>
                        <TableCell sx={{ maxWidth: 200 }} title={v.memo || ''}>{v.memo || '–'}</TableCell>
                        <TableCell align="right">{formatAmount(totalDebit)}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => navigate(`/finance/vouchers/${v.id}`)}>
                            View
                          </Button>
                          <Button size="small" onClick={() => navigate(`/finance/vouchers/${v.id}/edit`)}>
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default VouchersList;
